import * as THREE from 'three';

/**
 * Camera rig — the layer between raw player state and what the eye actually sees.
 *
 * Every offset here is additive and spring-damped rather than snapped, because the
 * difference between a shooter that feels expensive and one that feels like a demo
 * is almost entirely in these secondary motions: the weight of a landing, the way
 * recoil recovers along a curve instead of a line, how the world lags a fraction
 * behind a fast turn.
 *
 * Recoil is split in two: a *kick* applied to the aim (recovers, and is what the
 * player must fight) and a *visual* shake that never affects where bullets go.
 * Mixing them is the classic mistake that makes guns feel random instead of learnable.
 */

export const TUNING = {
  baseFov: 80,
  adsFovScale: 0.72,
  sprintFovBoost: 6,
  fovLerp: 9,

  viewFov: 60,
  viewAdsFovScale: 0.82,

  bobFreq: 2.05,
  bobAmpV: 0.038,
  bobAmpH: 0.031,
  bobRoll: 0.011,
  adsBobScale: 0.16,

  swayAmount: 0.0022,
  swayMax: 0.05,
  swayLerp: 7,

  landDip: 0.055,
  landSpring: 92,
  landDamp: 11,

  slideTilt: 0.075,
  strafeTilt: 0.017,
  tiltLerp: 8,

  /**
   * How fast the aim accumulator decays back toward centre.
   *
   * Was 9.5, which ate more than half the climb between two 725-RPM shots — the
   * accumulator hit equilibrium after a few rounds instead of walking, so a full
   * mag rose 0.5 degrees and the (excellent, learnable) recoil pattern was scaled
   * into irrelevance. Recovery is meant to be the reward for *stopping* firing,
   * not a free counter-force applied while you hold the trigger.
   */
  recoilRecovery: 4.0,
  /** Recovery is suppressed for this long after each shot, so sustained fire
   *  accumulates and only letting off the trigger brings the muzzle back down. */
  recoilHoldTime: 0.15,
  recoilSnappiness: 26,
  shakeDamp: 7.5,

  /**
   * Flinch: the aim punch taken when the player is shot.
   *
   * Kept as its own accumulator rather than folded into the recoil one for two
   * reasons. Mechanically, `WeaponSystem` drives `recoilTarget*` as a servo — it
   * publishes the *difference* between its own authoritative curve and whatever
   * this rig currently holds, so anything else added there is cancelled on the
   * very next frame. Conceptually they are different forces: recoil is the price
   * of your own trigger and is meant to be learnable, flinch is the price of
   * being hit and is meant to be fought.
   *
   * It is emphatically not trauma. Trauma is visual only and must never move the
   * point of aim; flinch moves it, which is the entire feature.
   */
  flinchSnappiness: 30,
  flinchRecovery: 5.5,

  breathAmp: 0.0016,
  breathFreq: 0.55,
};

export class CameraRig {
  constructor(ctx, player) {
    this.ctx = ctx;
    this.player = player;
    this.camera = ctx.camera;
    this.viewCamera = ctx.viewCamera;

    this.bobPhase = 0;
    this.bobWeight = 0;

    this.sway = new THREE.Vector2();
    this.swayTarget = new THREE.Vector2();

    // Landing spring (vertical dip).
    this.dip = 0;
    this.dipVel = 0;

    this.tilt = 0;
    this.tiltTarget = 0;

    // Aim recoil: an offset added to the player's look angles that decays back.
    this.recoilPitch = 0;
    this.recoilYaw = 0;
    this.recoilTargetPitch = 0;
    this.recoilTargetYaw = 0;

    // Flinch: a second aim offset, driven by damage rather than by the trigger.
    this.flinchPitch = 0;
    this.flinchYaw = 0;
    this.flinchTargetPitch = 0;
    this.flinchTargetYaw = 0;

    // Pure-visual shake, never affects aim.
    this.shake = new THREE.Vector3();
    this.shakeRot = 0;
    this.trauma = 0;

    this.fov = TUNING.baseFov;
    this.viewFov = TUNING.viewFov;

    this._quat = new THREE.Quaternion();
    this._euler = new THREE.Euler(0, 0, 0, 'YXZ');
    this._offset = new THREE.Vector3();
    this._prevYaw = 0;
    this._noiseT = Math.random() * 100;

    ctx.events.on('player:land', ({ impact }) => {
      this.dipVel -= Math.min(impact * 0.055, 0.9);
      this.addTrauma(Math.min(impact / 22, 0.5));
    });
    this._lastFireTime = -99;
    ctx.events.on('weapon:recoil', ({ pitch, yaw, kick, sustain }) => {
      this.recoilTargetPitch += pitch;
      this.recoilTargetYaw += yaw;
      this.addTrauma(kick ?? 0.12);
      // Sustain frames carry the ongoing climb and must not re-arm the hold, or
      // recovery could never start while a weapon was still settling.
      if (!sustain) this._lastFireTime = ctx.engine.elapsed;
    });
    ctx.events.on('player:damage', ({ amount }) => this.addTrauma(Math.min(amount / 45, 0.55)));
    // The aim punch itself arrives separately, already scaled by the incoming
    // damage and by flinch resistance — see `src/game/behaviours/Flinch.js`. This
    // rig only owns the curve it recovers along.
    ctx.events.on('player:flinch', ({ pitch, yaw }) => {
      if (Number.isFinite(pitch)) this.flinchTargetPitch += pitch;
      if (Number.isFinite(yaw)) this.flinchTargetYaw += yaw;
    });
    ctx.events.on('fx:explosion', ({ position, power }) => {
      const d = this.camera.position.distanceTo(position);
      this.addTrauma(THREE.MathUtils.clamp((power ?? 1) * (14 / (d + 4)), 0, 1));
    });

    // Runs in 'fx' so it sees the final simulated player state for the frame.
    ctx.engine.registerUpdate((dt, t) => this.update(dt, t), 'fx');
  }

  addTrauma(v) {
    this.trauma = Math.min(1, this.trauma + v);
  }

  update(dt, elapsed) {
    const p = this.player;

    /* ---- aim recoil: fast attack toward target, exponential recovery ---- */
    this.recoilPitch += (this.recoilTargetPitch - this.recoilPitch) * Math.min(1, TUNING.recoilSnappiness * dt);
    this.recoilYaw += (this.recoilTargetYaw - this.recoilYaw) * Math.min(1, TUNING.recoilSnappiness * dt);
    // Hold the accumulator while the trigger is live; only recover once the
    // player stops shooting. Decaying between shots is what flattened the climb.
    if (elapsed - this._lastFireTime > TUNING.recoilHoldTime) {
      const rec = Math.exp(-TUNING.recoilRecovery * dt);
      this.recoilTargetPitch *= rec;
      this.recoilTargetYaw *= rec;
    }

    /* ---- flinch: same shape, but it always recovers ---- */
    // No hold period. Recoil holds because sustained fire is supposed to stack;
    // flinch has nothing to stack toward, and holding it would make a burst of
    // chip damage something the player simply cannot shoot through.
    if (this.flinchTargetPitch !== 0 || this.flinchTargetYaw !== 0 || this.flinchPitch !== 0 || this.flinchYaw !== 0) {
      const k = Math.min(1, TUNING.flinchSnappiness * dt);
      this.flinchPitch += (this.flinchTargetPitch - this.flinchPitch) * k;
      this.flinchYaw += (this.flinchTargetYaw - this.flinchYaw) * k;
      const dec = Math.exp(-TUNING.flinchRecovery * dt);
      this.flinchTargetPitch *= dec;
      this.flinchTargetYaw *= dec;
      if (Math.abs(this.flinchTargetPitch) < 1e-7) this.flinchTargetPitch = 0;
      if (Math.abs(this.flinchTargetYaw) < 1e-7) this.flinchTargetYaw = 0;
      if (Math.abs(this.flinchPitch) < 1e-7) this.flinchPitch = 0;
      if (Math.abs(this.flinchYaw) < 1e-7) this.flinchYaw = 0;
    }

    /* ---- view bob ---- */
    const speed = p.speed;
    const moving = p.grounded && speed > 0.5 && p.state !== 1;
    const speedRatio = THREE.MathUtils.clamp(speed / 6.6, 0, 1.35);
    this.bobWeight = THREE.MathUtils.damp(this.bobWeight, moving ? 1 : 0, 8, dt);
    if (moving) this.bobPhase += dt * TUNING.bobFreq * Math.PI * 2 * (0.65 + speedRatio * 0.9);

    const adsScale = p.ads ? TUNING.adsBobScale : 1;
    const bobAmt = this.bobWeight * speedRatio * adsScale;
    // Vertical bob runs at 2x the horizontal phase — one dip per footfall.
    const bobY = Math.sin(this.bobPhase * 2) * TUNING.bobAmpV * bobAmt;
    const bobX = Math.cos(this.bobPhase) * TUNING.bobAmpH * bobAmt;
    const bobRoll = Math.cos(this.bobPhase) * TUNING.bobRoll * bobAmt;

    /* ---- idle breathing (only when nearly still, so it reads as calm) ---- */
    const stillness = 1 - THREE.MathUtils.clamp(speed / 1.5, 0, 1);
    const breath =
      Math.sin(elapsed * TUNING.breathFreq * Math.PI * 2) * TUNING.breathAmp * stillness * (p.ads ? 0.45 : 1);

    /* ---- landing dip spring ---- */
    this.dipVel += -TUNING.landSpring * this.dip * dt - TUNING.landDamp * this.dipVel * dt;
    this.dip += this.dipVel * dt;

    /* ---- turn sway: the view lags behind fast mouse movement ---- */
    const yawDelta = p.yaw - this._prevYaw;
    this._prevYaw = p.yaw;
    this.swayTarget.x = THREE.MathUtils.clamp(-yawDelta / Math.max(dt, 1e-4) * TUNING.swayAmount, -TUNING.swayMax, TUNING.swayMax);
    this.swayTarget.y = THREE.MathUtils.clamp(this.dipVel * 0.04, -TUNING.swayMax, TUNING.swayMax);
    this.sway.x = THREE.MathUtils.damp(this.sway.x, this.swayTarget.x, TUNING.swayLerp, dt);
    this.sway.y = THREE.MathUtils.damp(this.sway.y, this.swayTarget.y, TUNING.swayLerp, dt);

    /* ---- roll: strafe lean + slide tilt ---- */
    const strafe = this._strafeAmount();
    this.tiltTarget = -strafe * TUNING.strafeTilt + (p.state === 1 ? TUNING.slideTilt : 0);
    if (p.ads) this.tiltTarget *= 0.35;
    this.tilt = THREE.MathUtils.damp(this.tilt, this.tiltTarget, TUNING.tiltLerp, dt);

    /* ---- trauma shake: quadratic falloff reads as an impact, not a vibration ---- */
    this.trauma = Math.max(0, this.trauma - dt * TUNING.shakeDamp * 0.14);
    const s = this.trauma * this.trauma;
    this._noiseT += dt * 34;
    this.shake.set(
      (perlinish(this._noiseT) * 2 - 1) * s * 0.055,
      (perlinish(this._noiseT + 31.7) * 2 - 1) * s * 0.055,
      0
    );
    this.shakeRot = (perlinish(this._noiseT + 71.3) * 2 - 1) * s * 0.028;

    /* ---- assemble orientation ---- */
    // Flinch rides alongside recoil in the aim sum, not in the shake: it has to
    // take the crosshair off the target, or it is only a light show.
    const pitch = THREE.MathUtils.clamp(
      p.pitch + this.recoilPitch + this.flinchPitch + this.sway.y * 0.35 + breath,
      -Math.PI / 2 + 0.01,
      Math.PI / 2 - 0.01
    );
    const yaw = p.yaw + this.recoilYaw + this.flinchYaw + this.sway.x * 0.5 + breath * 0.6;
    const roll = this.tilt + bobRoll + this.shakeRot;

    this._euler.set(pitch, yaw, roll);
    this._quat.setFromEuler(this._euler);
    this.camera.quaternion.copy(this._quat);

    /* ---- position ---- */
    const eye = p.position.y + p.eye;
    this._offset.set(bobX + this.shake.x, bobY + this.dip + this.shake.y, 0).applyQuaternion(this._quat);
    this.camera.position.set(p.position.x, eye, p.position.z).add(this._offset);

    /* ---- FOV: ADS pulls in, sprint pushes out ---- */
    let targetFov = TUNING.baseFov;
    if (p.ads) targetFov = TUNING.baseFov * (this.adsFovScale ?? TUNING.adsFovScale);
    else if (p.tacSprinting) targetFov += TUNING.sprintFovBoost;
    else if (p.sprinting) targetFov += TUNING.sprintFovBoost * 0.55;
    // Speed alone contributes a little, so vaulting and slides feel fast.
    targetFov += THREE.MathUtils.clamp((speed - 4) * 0.55, 0, 4);

    this.fov = THREE.MathUtils.damp(this.fov, targetFov, TUNING.fovLerp, dt);
    if (Math.abs(this.camera.fov - this.fov) > 0.01) {
      this.camera.fov = this.fov;
      this.camera.updateProjectionMatrix();
    }

    const targetViewFov = TUNING.viewFov * (p.ads ? TUNING.viewAdsFovScale : 1);
    this.viewFov = THREE.MathUtils.damp(this.viewFov, targetViewFov, TUNING.fovLerp, dt);
    if (Math.abs(this.viewCamera.fov - this.viewFov) > 0.01) {
      this.viewCamera.fov = this.viewFov;
      this.viewCamera.updateProjectionMatrix();
    }

    // The viewmodel camera sits at the origin; the viewmodel itself is parented to
    // it in view space, so it needs no world transform — only the rotation used for
    // sway is applied inside ViewModel. Keeping this identity means the gun can
    // never be clipped by world geometry.
    this.viewCamera.position.set(0, 0, 0);
    this.viewCamera.quaternion.identity();
  }

  /** -1 (left) .. 1 (right) relative to facing, for camera lean. */
  _strafeAmount() {
    const p = this.player;
    const right = _r.set(Math.cos(p.yaw), 0, -Math.sin(p.yaw));
    const v = _v.set(p.velocity.x, 0, p.velocity.z);
    const speed = v.length();
    if (speed < 0.2) return 0;
    return THREE.MathUtils.clamp(v.dot(right) / Math.max(speed, 1e-3), -1, 1) * Math.min(1, speed / 4);
  }
}

/** Cheap smooth pseudo-noise; good enough for shake and far cheaper than real Perlin. */
function perlinish(t) {
  return (
    (Math.sin(t * 1.0) + Math.sin(t * 2.31 + 1.7) * 0.5 + Math.sin(t * 4.77 + 3.1) * 0.25) / 1.75 * 0.5 + 0.5
  );
}

const _r = new THREE.Vector3();
const _v = new THREE.Vector3();
